import { StackChildren, Inset } from '@doordash/design-language'
import * as React from 'react'
import { Modal } from '@doordash/component-modal'
import Separator from '@doordash/component-separator'
import NavBar from '@doordash/component-navbar'
import Button from '@doordash/component-button'
import { safetyCourse as t } from './strings'
import * as DA from '../../../typeModules/events'
import { SafetySection } from './SafetySection'
import { FoodSafetySection } from './FoodSafetySection'
import { SafeDrivingSection } from './SafeDrivingSection'
import { SexualHarassmentSection } from './SexualHarassmentSection'
import { SubmitSection } from './SubmitSection'
import { SlideButtonContainer, ProgressContainer } from './styles'
import { generateProgress } from './images/ProgressIndicatorImage'

type SafetyCourseStepProps = {
  completeSafetyCourse?: any
  onClose?: any
  safetyCourseStep?: {
    isSubmitting: boolean
    isSubmitted: boolean
    error?: null
  }
  buttonState: string

  track?: (
    action: DA.EventActions,
    name: DA.SafetyCourseSections,
    courseId: number,
    data?: object
  ) => Promise<any>
  onSubmit?: () => Promise<any>
  completeCourse?: () => Promise<any>
  courseId?: number
  currentSlide?: number
}

export const SafetyCourse: React.FC<SafetyCourseStepProps> = ({
  onClose,
  track,
  completeCourse,
  onSubmit,
  buttonState,
  courseId,
  currentSlide,
}) => {
  const SLIDE_DECK_COURSE_ID = 2
  const SLIDE_DECK = [
    <SafetySection />,
    <FoodSafetySection />,
    <SafeDrivingSection />,
    <SexualHarassmentSection />,
    <SubmitSection
      track={track}
      completeCourse={() => completeCourse()}
      onSubmit={() => onSubmit()}
      courseId={courseId}
      buttonState={buttonState}
    />,
  ]
  const [slide, setSlide] = React.useState(currentSlide)
  track(DA.EventActions.Rendered, DA.SafetyCourseSections.Course, courseId)

  if (courseId === SLIDE_DECK_COURSE_ID) {
    const LAST_SLIDE = SLIDE_DECK.length - 1
    const myRef = React.useRef(null)
    const executeScroll = () => myRef.current.scrollIntoView(true)
    return (
      <Modal
        title={t.title()}
        hasContentPadding={false}
        onClose={() => {
          track(
            DA.EventActions.Close,
            DA.SafetyCourseSections.Course,
            courseId,
            { slide }
          )
          onClose(slide)
        }}
        hasCloseButton={false}
        headerImage={false}
        headerAlignment={Modal.HeaderAlignment.Center}
        shouldRenderStickyHeader={false}
        renderContentHeader={({ title }) => (
          <NavBar
            onNavigationClick={() => {
              track(
                DA.EventActions.Close,
                DA.SafetyCourseSections.Course,
                courseId,
                { slide }
              )
              onClose(slide)
            }}
            textAlign={NavBar.TextAlignments.Center}
            isSeparatorVisible={false}
            headerImage={false}
            navigationType={NavBar.NavigationTypes.Close}
            title={title}
          />
        )}
      >
        <div ref={myRef}>
          <Separator type={Separator.Types.FullBorder} />
          <ProgressContainer>{generateProgress(slide + 1)}</ProgressContainer>
          <Separator type={Separator.Types.FullBorder} />
        </div>
        {SLIDE_DECK[slide]}
        <Inset>
          <SlideButtonContainer>
            {slide !== 0 && slide !== LAST_SLIDE && (
              <Button
                isInline
                type={Button.Types.Tertiary}
                onClick={() => {
                  track(
                    DA.EventActions.Previous,
                    DA.SafetyCourseSections.Course,
                    courseId,
                    { slide }
                  )
                  setSlide(slide - 1)
                  executeScroll()
                }}
              >
                {t.previous()}
              </Button>
            )}
            {slide !== LAST_SLIDE && (
              <Button
                isInline={slide !== 0}
                type={Button.Types.PrimaryPill}
                onClick={() => {
                  track(
                    DA.EventActions.Next,
                    DA.SafetyCourseSections.Course,
                    courseId,
                    { slide }
                  )
                  setSlide(slide + 1)
                  executeScroll()
                }}
              >
                {slide !== 0 ? t.next() : t.continue()}
              </Button>
            )}
          </SlideButtonContainer>
        </Inset>
      </Modal>
    )
  }

  return (
    <Modal
      title={t.title()}
      hasContentPadding={false}
      onClose={() => onClose()}
      hasCloseButton={false}
      headerImage={false}
      headerAlignment={Modal.HeaderAlignment.Center}
      renderContentHeader={({ title }) => (
        <NavBar
          onNavigationClick={() => onClose()}
          textAlign={NavBar.TextAlignments.Center}
          isSeparatorVisible={false}
          headerImage={false}
          navigationType={NavBar.NavigationTypes.Close}
          title={title}
        />
      )}
    >
      <Separator type={Separator.Types.FullBorder} />
      <StackChildren>
        {SLIDE_DECK.map((slide, index, arr) => (
          <React.Fragment>
            {slide}
            <Separator
              type={
                // if the next component is the last one, do a seperator instead of full
                index + 1 === arr.length - 1
                  ? Separator.Types.Separator
                  : Separator.Types.FullBorder
              }
            />
          </React.Fragment>
        ))}
      </StackChildren>
    </Modal>
  )
}

export default SafetyCourse
